import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, Globe, Shield, TrendingUp, Zap, CheckCircle, Linkedin, Twitter, MessageCircle, ArrowRight, Target, Users, BarChart } from 'lucide-react';
import logo from './logo.png';
import profile from './profile.jpg'; 

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // We initialize the page based on the current URL hash (e.g., #about)
  // If no hash exists, default to 'home'
  const [currentPage, setCurrentPage] = useState(window.location.hash.replace('#', '') || 'home');

  useEffect(() => {
    // Function to handle URL changes (Back button, Forward button, or Link clicks)
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'home';
      setCurrentPage(hash);
      window.scrollTo(0, 0); // Always scroll to top on page change
      setIsMenuOpen(false);  // Close mobile menu if open
    };

    // 1. Set the correct page immediately when the site loads
    handleHashChange();

    // 2. Listen for "hashchange" events (This makes the Back button work!)
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup listener when app closes
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // --- PAGE CONTENT COMPONENTS ---

  const Home = () => (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <header className="bg-white py-20 lg:py-32 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-gold-600 font-bold tracking-widest uppercase text-sm mb-4 block">
            Financial Protection & Growth Strategist
          </span>
          <h1 className="text-5xl md:text-7xl font-extrabold text-navy-900 mb-8 leading-tight">
            Protect Assets. <br/>
            <span className="text-gold-500">Grow Wealth.</span> <br/>
            Empower Sales.
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600 mb-10 leading-relaxed">
            I help high-net-worth individuals secure their legacy and business owners dominate their market. 
            Bridging the gap between financial security and scalable revenue.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4 px-6 md:px-0">
            <a 
              href="#contact"
              className="px-8 py-4 bg-navy-900 text-white font-bold rounded shadow-lg hover:bg-gold-500 hover:text-navy-900 transition-all inline-block"
            >
              Start a Conversation
            </a>
            <a 
              href="#services"
              className="px-8 py-4 border-2 border-navy-900 text-navy-900 font-bold rounded hover:bg-gray-50 transition-all inline-block"
            >
              Explore Solutions
            </a>
          </div>
        </div>
      </header>

      {/* Value Proposition Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <Shield size={40} className="text-gold-500 mb-6" />
              <h3 className="text-xl font-bold text-navy-900 mb-3">Risk Management</h3>
              <p className="text-gray-600">Comprehensive analysis of personal and business liabilities to ensure you are never exposed to ruin.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <TrendingUp size={40} className="text-gold-500 mb-6" />
              <h3 className="text-xl font-bold text-navy-900 mb-3">Wealth Architecture</h3>
              <p className="text-gray-600">Structuring investments to outpace inflation and create inter-generational wealth transfer.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <Zap size={40} className="text-gold-500 mb-6" />
              <h3 className="text-xl font-bold text-navy-900 mb-3">Sales Acceleration</h3>
              <p className="text-gray-600">Diagnosing sales bottlenecks and training teams to close high-ticket deals consistently.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const About = () => (
    <div className="animate-fade-in py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row gap-16 items-start">
          
          {/* Image Side */}
          <div className="md:w-1/3 relative">
            <div className="absolute inset-0 bg-gold-500 rounded-lg transform translate-x-3 translate-y-3"></div>
            <img 
              src={profile} 
              alt="Deepak Shetty" 
              className="relative rounded-lg shadow-2xl w-full border border-gray-200" 
            />
            
            <div className="mt-8 bg-navy-50 p-6 rounded-lg border-l-4 border-gold-500">
              <h4 className="font-bold text-navy-900 mb-2">My Philosophy</h4>
              <p className="text-sm text-gray-700 italic">
                "Revenue solves all business problems, but Insurance solves the problems money can't fix. You need both to survive and thrive."
              </p>
            </div>
          </div>

          {/* Content Side */}
          <div className="md:w-2/3">
            <h2 className="text-4xl font-bold text-navy-900 mb-6">About Deepak Shetty</h2>
            <div className="prose prose-lg text-gray-600 space-y-6">
              <p>
                I am an Insurance Professional and Sales Strategist based in India, dedicated to helping individuals secure their financial future and businesses scale their revenue operations.
              </p>
              <p>
                My journey began in the high-pressure world of sales, where I learned that the lifeblood of any organization is its ability to generate revenue. However, I also witnessed how fragile that success can be without a safety net. This realization led me to found <strong>Dhriti Enterprises</strong>.
              </p>
              <p>
                Today, I operate at the intersection of <strong>Protection</strong> and <strong>Performance</strong>.
              </p>
              <ul className="grid sm:grid-cols-2 gap-4 mt-8">
                <li className="flex items-center text-navy-900 font-medium">
                  <CheckCircle size={20} className="text-gold-500 mr-3" />
                  Certified Insurance Advisor
                </li>
                <li className="flex items-center text-navy-900 font-medium">
                  <CheckCircle size={20} className="text-gold-500 mr-3" />
                  Corporate Sales Trainer
                </li>
                <li className="flex items-center text-navy-900 font-medium">
                  <CheckCircle size={20} className="text-gold-500 mr-3" />
                  GTM Strategy Consultant
                </li>
                <li className="flex items-center text-navy-900 font-medium">
                  <CheckCircle size={20} className="text-gold-500 mr-3" />
                  Wealth Planner
                </li>
              </ul>
              
              <div className="pt-8 mt-8 border-t border-gray-100">
                <a 
                  href="#contact"
                  className="inline-flex items-center font-bold text-navy-900 hover:text-gold-600 transition-colors"
                >
                  Get in touch with me <ArrowRight className="ml-2" size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const Services = () => (
    <div className="animate-fade-in bg-gray-50 pb-20">
      
      {/* Header */}
      <div className="bg-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">The Three Pillars</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">A holistic approach to personal finance and business growth.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 space-y-20">
        
        {/* PILLAR 1: PROTECT */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/3 bg-blue-50 p-10 flex flex-col justify-center border-r border-blue-100">
            <Shield size={64} className="text-navy-900 mb-6" />
            <h3 className="text-3xl font-bold text-navy-900 mb-4">PROTECT</h3>
            <p className="text-gray-600">The foundation of all financial planning. We build a fortress around your assets.</p>
          </div>
          <div className="md:w-2/3 p-10">
            <h4 className="text-xl font-bold text-navy-900 mb-6 flex items-center">
              <Target className="mr-2 text-gold-500"/> Core Solutions
            </h4>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h5 className="font-bold text-gray-900 mb-2">Personal Risk</h5>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Term Life Insurance (High Cover)</li>
                  <li>• Comprehensive Health Insurance</li>
                  <li>• Critical Illness Covers</li>
                  <li>• Personal Accident Policies</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-gray-900 mb-2">Business Risk</h5>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Keyman Insurance</li>
                  <li>• Director's Liability (D&O)</li>
                  <li>• Group Health for Employees</li>
                  <li>• Fire & Marine Commercial Policies</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* PILLAR 2: GROW */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/3 bg-gold-50 p-10 flex flex-col justify-center border-r border-gold-100">
            <TrendingUp size={64} className="text-navy-900 mb-6" />
            <h3 className="text-3xl font-bold text-navy-900 mb-4">GROW</h3>
            <p className="text-gray-600">Strategic wealth creation. Moving beyond savings into true compounding.</p>
          </div>
          <div className="md:w-2/3 p-10">
            <h4 className="text-xl font-bold text-navy-900 mb-6 flex items-center">
              <BarChart className="mr-2 text-gold-500"/> Investment Advisory
            </h4>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h5 className="font-bold text-gray-900 mb-2">Wealth Creation</h5>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Mutual Fund Portfolio Review</li>
                  <li>• Direct Equity Advisory</li>
                  <li>• Alternative Investment Funds (AIF)</li>
                  <li>• Goal-based Financial Planning</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-gray-900 mb-2">Legacy Planning</h5>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Estate Planning Services</li>
                  <li>• Will Drafting & Registration</li>
                  <li>• Family Trust Creation</li>
                  <li>• Business Succession Planning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* PILLAR 3: EMPOWER */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/3 bg-gray-100 p-10 flex flex-col justify-center border-r border-gray-200">
            <Zap size={64} className="text-navy-900 mb-6" />
            <h3 className="text-3xl font-bold text-navy-900 mb-4">EMPOWER</h3>
            <p className="text-gray-600">Sales is the engine of your business. We tune it for maximum performance.</p>
          </div>
          <div className="md:w-2/3 p-10">
            <h4 className="text-xl font-bold text-navy-900 mb-6 flex items-center">
              <Users className="mr-2 text-gold-500"/> Sales Consulting
            </h4>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h5 className="font-bold text-gray-900 mb-2">Training & Development</h5>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• B2B Sales Masterclass</li>
                  <li>• Negotiation Skills Workshop</li>
                  <li>• High-Ticket Closing Strategies</li>
                  <li>• Sales Leadership Coaching</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-gray-900 mb-2">Process & Strategy</h5>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Go-To-Market (GTM) Strategy</li>
                  <li>• Sales Funnel Optimization</li>
                  <li>• CRM Implementation Advisory</li>
                  <li>• Incentive Structure Design</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  const Contact = () => (
    <div className="animate-fade-in py-20 bg-gray-50 min-h-[60vh]">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-navy-900 p-10 text-center">
            <h2 className="text-3xl text-white font-bold mb-2">Ready to Secure Your Future?</h2>
            <p className="text-gray-300">Schedule a consultation or reach out directly.</p>
          </div>
          
          <div className="p-10 grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <h3 className="text-xl font-bold text-navy-900 border-b border-gray-200 pb-2">Contact Details</h3>
              
              <a href="tel:+919535693323" className="flex items-center group">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gold-500 transition-colors">
                  <Phone size={24} className="text-navy-900" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Call / WhatsApp</p>
                  <p className="text-lg font-bold text-navy-900">+91 95356 93323</p>
                </div>
              </a>

              <a href="mailto:hello@deepakshetty.in" className="flex items-center group">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gold-500 transition-colors">
                  <Mail size={24} className="text-navy-900" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Email Me</p>
                  <p className="text-lg font-bold text-navy-900">hello@deepakshetty.in</p>
                </div>
              </a>

              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Globe size={24} className="text-navy-900" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="text-lg font-bold text-navy-900">Serving India Nationwide</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl flex flex-col justify-center text-center">
              <h3 className="text-xl font-bold text-navy-900 mb-4">Book a Strategy Call</h3>
              <p className="text-gray-600 mb-6">
                Connect with me to discuss your insurance needs or business sales strategy.
              </p>
              <a 
                href="mailto:hello@deepakshetty.in?subject=Consultation Request" 
                className="bg-navy-900 text-white font-bold py-3 px-6 rounded hover:bg-gold-500 hover:text-navy-900 transition-all"
              >
                Send Request via Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col font-sans relative bg-white">
      
      {/* Floating WhatsApp Button */}
      <a 
        href="https://wa.me/919535693323" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-600 text-white p-4 rounded-full shadow-2xl hover:bg-green-700 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={32} />
      </a>

      {/* Navigation Bar */}
      <nav className="bg-navy-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-32 items-center"> 
            
            {/* Logo Area */}
            <a href="#home" className="flex items-center">
              <div className="bg-white p-2 rounded-lg shadow-lg w-fit">
                <img 
                  src={logo} 
                  alt="Deepak Shetty" 
                  className="h-24 w-auto object-contain" 
                />
              </div>
            </a>

            {/* Desktop Menu - Using href='#link' for history support */}
            <div className="hidden md:flex space-x-8 items-center">
              <a href="#home" className={`hover:text-gold-500 transition-colors ${currentPage === 'home' ? 'text-gold-500 font-bold' : 'text-gray-300'}`}>Home</a>
              <a href="#about" className={`hover:text-gold-500 transition-colors ${currentPage === 'about' ? 'text-gold-500 font-bold' : 'text-gray-300'}`}>About</a>
              <a href="#services" className={`hover:text-gold-500 transition-colors ${currentPage === 'services' ? 'text-gold-500 font-bold' : 'text-gray-300'}`}>Services</a>
              <a 
                href="#contact"
                className="bg-gold-500 text-navy-900 px-5 py-2 rounded font-semibold hover:bg-white transition-colors"
              >
                Book Consultation
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white">
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden bg-navy-800 px-4 pt-2 pb-6 space-y-4 shadow-xl border-t border-navy-700">
            <a href="#home" className="block text-white hover:text-gold-500 py-2 w-full text-left">Home</a>
            <a href="#about" className="block text-white hover:text-gold-500 py-2 w-full text-left">About</a>
            <a href="#services" className="block text-white hover:text-gold-500 py-2 w-full text-left">Services</a>
            <a href="#contact" className="block text-gold-500 font-bold py-2 w-full text-left">Book Consultation</a>
          </div>
        )}
      </nav>

      {/* Main Content Render */}
      <main className="flex-grow">
        {currentPage === 'home' && <Home />}
        {currentPage === 'about' && <About />}
        {currentPage === 'services' && <Services />}
        {currentPage === 'contact' && <Contact />}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            
            <div>
              <h4 className="text-2xl font-bold text-white mb-2">DEEPAK SHETTY</h4>
              <p className="text-gold-500 text-sm mb-6">FINANCIAL PROTECTION & GROWTH STRATEGIST</p>
              
              <div className="flex justify-center md:justify-start space-x-4">
                <a href="#" className="text-gray-400 hover:text-gold-500 transition-colors">
                  <Linkedin size={24} />
                </a>
                <a href="#" className="text-gray-400 hover:text-gold-500 transition-colors">
                  <Twitter size={24} />
                </a>
              </div>
            </div>
            
            <div className="flex flex-col items-center md:items-start space-y-4">
              <a href="tel:+919535693323" className="flex items-center hover:text-gold-500 transition-colors">
                <Phone size={20} className="mr-3 text-gold-500" /> +91 95356 93323
              </a>
              <a href="mailto:hello@deepakshetty.in" className="flex items-center hover:text-gold-500 transition-colors">
                <Mail size={20} className="mr-3 text-gold-500" /> hello@deepakshetty.in
              </a>
              <span className="flex items-center text-gray-400">
                <Globe size={20} className="mr-3 text-gold-500" /> deepakshetty.in
              </span>
            </div>

            <div className="flex flex-col justify-center">
              <a 
                href="#contact"
                className="bg-gold-500 text-navy-900 font-bold py-3 px-6 rounded hover:bg-white transition-colors w-full md:w-auto text-center"
              >
                Schedule a Call
              </a>
              <p className="text-xs text-gray-500 mt-4 text-center">
                © {new Date().getFullYear()} Deepak Shetty. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;